import styled from "styled-components";
import {
    TextInput as PlaylistModalTextInput,
    TextArea as PlaylistModalTextArea,
} from "src/scenes/Playlists/components/shared";
import { GradientActionButton, PrimaryAction } from "./shell";
import { dashboardTheme } from "./theme";
import type { ThemeMode } from "../types";

type ThemeModeProps = {
    themeMode?: ThemeMode;
    $hasPickerIcon?: boolean;
    $trailingActionCount?: number;
};

type ToneProps = {
    background?: string;
    color?: string;
};

type ActiveProps = {
    active?: boolean;
};

type PickerFieldIconProps = {
    icon: "date" | "time";
};

const DATE_PICKER_MASK = "url(\"data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2216%22 height=%2216%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22black%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3E%3Crect x=%223%22 y=%224%22 width=%2218%22 height=%2218%22 rx=%222%22 ry=%222%22/%3E%3Cline x1=%2216%22 y1=%222%22 x2=%2216%22 y2=%226%22/%3E%3Cline x1=%228%22 y1=%222%22 x2=%228%22 y2=%226%22/%3E%3Cline x1=%223%22 y1=%2210%22 x2=%2221%22 y2=%2210%22/%3E%3C/svg%3E\") center / contain no-repeat";
const TIME_PICKER_MASK = "url(\"data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2216%22 height=%2216%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22black%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22%3E%3Ccircle cx=%2212%22 cy=%2212%22 r=%229%22/%3E%3Cpolyline points=%2212 7 12 12 15 15%22/%3E%3C/svg%3E\") center / contain no-repeat";

export const ModalCard = styled.div<ThemeModeProps>`
    ${dashboardTheme};
    max-height: 88vh;
    background: ${p => p.themeMode === "dark"
        ? "linear-gradient(180deg, #10131d 0%, #161a24 100%)"
        : "linear-gradient(180deg, #ffffff 0%, #f7f9fd 100%)"};
    color: var(--calendar-text);
    display: flex;
    flex-direction: column;
    border: 1px solid var(--calendar-border-strong);
    border-radius: var(--calendar-radius-shell);
    overflow: hidden;
    box-shadow: 0 28px 70px rgba(0, 0, 0, 0.28);
    position: relative;
`;

export const ModalHeaderBlock = styled.div<ThemeModeProps>`
    padding: 1.5rem 1.6rem 1.2rem;
    border-bottom: 1px solid var(--calendar-border);
    background: transparent;
`;

export const ModalScroll = styled.div`
    overflow-y: auto;
    padding: 1.2rem 1.6rem 1.5rem;
    display: grid;
    gap: 1.1rem;
`;

export const ModalFooter = styled.div<ThemeModeProps>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.6rem 1.35rem;
    border-top: 1px solid var(--calendar-border);
    flex-wrap: wrap;
    background: transparent;
`;

export const ModalHeaderTop = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
`;

export const ModalTitleBlock = styled.div`
    display: grid;
    gap: 0.45rem;
`;

export const ModalTitle = styled.div`
    font-size: 28px;
    line-height: 1.05;
    font-weight: 800;
`;

export const ModalHeroPills = styled.div`
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    justify-content: flex-end;
`;

export const ModalHeroPill = styled.div<ToneProps>`
    padding: 0.4rem 0.65rem;
    border-radius: var(--calendar-radius-badge);
    background: ${p => p.background || "var(--calendar-surface-tertiary)"};
    color: ${p => p.color || "var(--calendar-text-soft)"};
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.02em;
`;

export const ModalSection = styled.div<ThemeModeProps>`
    border: 1px solid var(--calendar-border);
    background: ${p => p.themeMode === "dark" ? "rgba(27, 32, 48, 0.78)" : "rgba(243, 245, 251, 0.95)"};
    border-radius: var(--calendar-radius-card);
    padding: 1rem;
    display: grid;
    gap: 0.9rem;
`;

export const ModalSectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
`;

export const ModalSectionTitle = styled.div`
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--calendar-text-soft);
`;

export const ModalSectionNote = styled.div`
    font-size: 11px;
    color: var(--calendar-text-soft);
`;

export const AttachmentRow = styled.div`
    display: flex;
    gap: 0.75rem;
    align-items: center;
    flex-wrap: wrap;
`;

export const HiddenFileInput = styled.input`
    display: none;
`;

export const AttachmentList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.85rem;
    width: 100%;
`;

export const AttachmentBadge = styled.div<ThemeModeProps>`
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.4rem 0.65rem;
    border-radius: var(--calendar-radius-badge);
    background: ${p => p.themeMode === "dark" ? "rgba(109, 116, 255, 0.22)" : "rgba(91, 110, 245, 0.16)"};
    color: ${p => p.themeMode === "dark" ? "#dfe3ff" : "#4e5fe0"};
    border: 1px solid ${p => p.themeMode === "dark" ? "#6d74ff" : "#5b6ef5"};
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.02em;
    white-space: nowrap;
`;

export const AttachmentName = styled.div`
    display: inline-block;
    align-items: center;
    line-height: 1;
    max-width: 480px;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const AttachmentRemove = styled.button`
    border: none;
    background: transparent;
    color: var(--calendar-text-soft);
    width: 16px;
    height: 16px;
    border-radius: var(--calendar-radius-control);
    padding: 0;
    line-height: 1;
    font-size: 13px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
`;

export const FormGrid = styled.div<{ columns?: number }>`
    display: grid;
    grid-template-columns: repeat(${p => p.columns || 2}, minmax(0, 1fr));
    gap: 0.85rem;

    @media (max-width: 760px) {
        grid-template-columns: 1fr;
    }
`;

export const VisibilityTargetGrid = styled.div`
    display: grid;
    gap: 0.75rem;
    margin-top: 0.85rem;
`;

export const VisibilityTargetCard = styled.div`
    border: 1px solid var(--calendar-border);
    background: var(--calendar-surface);
    border-radius: var(--calendar-radius-card);
    padding: 0.85rem;
    display: grid;
    gap: 0.75rem;
`;

export const VisibilityTargetHeader = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.85rem;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

export const VisibilityTargetTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 0.55rem;
    min-width: 0;
    font-size: 13px;
    font-weight: 800;
`;

export const VisibilityTargetActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.55rem;
    align-items: center;
`;

export const VisibilityPickerPanel = styled.div<ThemeModeProps>`
    border: 1px solid var(--calendar-border-strong);
    background: ${p => p.themeMode === "dark" ? "rgba(16, 19, 29, 0.96)" : "rgba(255, 255, 255, 0.96)"};
    border-radius: var(--calendar-radius-card);
    padding: 0.85rem;
    display: grid;
    gap: 0.75rem;
`;

export const VisibilityPickerHeader = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
    gap: 0.85rem;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

export const VisibilityUserList = styled.div`
    display: grid;
    gap: 0.45rem;
    max-height: 280px;
    overflow-y: auto;
    padding-right: 0.15rem;
`;

export const VisibilityUserRow = styled.button<ActiveProps>`
    width: 100%;
    border: 1px solid ${p => p.active ? "var(--calendar-accent)" : "var(--calendar-border)"};
    background: ${p => p.active ? "var(--calendar-accent-soft)" : "var(--calendar-surface-secondary)"};
    color: var(--calendar-text);
    border-radius: var(--calendar-radius-card);
    padding: 0.65rem 0.75rem;
    display: grid;
    grid-template-columns: 34px minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.75rem;
    text-align: left;
    cursor: pointer;

    > div:nth-child(2) {
        min-width: 0;
        overflow-wrap: anywhere;
    }
`;

export const VisibilityUserAvatar = styled.div<ToneProps>`
    width: 34px;
    height: 34px;
    border-radius: var(--calendar-radius-round);
    background: ${p => p.background || "var(--calendar-accent-soft)"};
    color: ${p => p.color || "var(--calendar-accent)"};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 800;
`;

export const VisibilityEmptyState = styled.div`
    border: 1px dashed var(--calendar-border-strong);
    background: var(--calendar-surface-secondary);
    border-radius: var(--calendar-radius-card);
    padding: 0.85rem;
`;

export const TimePickerWrap = styled.div`
    position: relative;
`;

export const PickerFieldWrap = styled.div`
    position: relative;
`;

export const PickerFieldIcon = styled.span<PickerFieldIconProps>`
    position: absolute;
    right: 14px;
    top: 50%;
    width: 16px;
    height: 16px;
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 3;
    background-color: var(--calendar-text-soft);
    mask: ${p => p.icon === "date" ? DATE_PICKER_MASK : TIME_PICKER_MASK};
    -webkit-mask: ${p => p.icon === "date" ? DATE_PICKER_MASK : TIME_PICKER_MASK};
`;

export const TimePickerPanel = styled.div<ThemeModeProps>`
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% + 8px);
    z-index: 60;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.25rem;
    max-height: 240px;
    overflow-y: auto;
    padding: 0.45rem;
    border: 1px solid var(--calendar-border-strong);
    border-radius: var(--calendar-radius-card);
    background: ${p => p.themeMode === "dark" ? "rgba(16, 19, 29, 0.98)" : "rgba(255, 255, 255, 0.98)"};
    box-shadow: 0 18px 36px rgba(15, 23, 42, 0.22);
`;

export const TimePickerOption = styled.button<{ active?: boolean }>`
    min-height: 32px;
    border-radius: var(--calendar-radius-control);
    border: 1px solid ${p => p.active ? "var(--calendar-selected-cell-border)" : "transparent"};
    background: ${p => p.active ? "var(--calendar-selected-cell-bg)" : "transparent"};
    color: var(--calendar-text);
    cursor: pointer;
    font-size: 12px;
    font-weight: 700;

    &:hover {
        border-color: var(--calendar-border-strong);
        background: var(--calendar-surface-secondary);
    }
`;

export const Label = styled.label`
    display: block;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.3;
    color: var(--calendar-text);
    margin-bottom: 0.5rem;
    text-transform: none;
    letter-spacing: normal;
`;

export const ModalPrimaryButton = styled(PrimaryAction)<ThemeModeProps>`
    min-width: 120px;
    box-shadow: none;

    &:disabled {
        opacity: 0.52;
        cursor: not-allowed;
    }
`;

export const ModalGradientButton = styled(GradientActionButton)<ThemeModeProps>`
    min-width: 120px;

    &:disabled {
        opacity: 0.52;
        cursor: not-allowed;
    }
`;

export const SuggestionRestoreButton = styled(ModalPrimaryButton)`
    border-radius: var(--calendar-radius-badge);
    min-height: 34px;
    height: 34px;
    padding: 0.45rem 0.85rem;
    font-size: 11px;
    font-weight: 800;
    line-height: 1.2;
    min-width: 0;
`;

export const ModalCancelButton = styled.button<ThemeModeProps>`
    height: 42px;
    min-width: 120px;
    border-radius: var(--calendar-radius-control);
    border: 1px solid var(--calendar-border-strong);
    background: ${p => p.themeMode === "dark" ? "rgba(19, 25, 39, 0.9)" : "rgba(255, 255, 255, 0.92)"};
    color: var(--calendar-text);
    padding: 0 1.1rem;
    box-sizing: border-box;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
`;

export const TaskFieldInput = styled(PlaylistModalTextInput)<ThemeModeProps>`
    && {
    color: var(--calendar-text);
    -webkit-text-fill-color: var(--calendar-text);
    caret-color: var(--calendar-text);
    text-align: left;
    background-color: var(--calendar-surface-tertiary);
    width: 100%;
    height: 46px;
    padding: ${p => {
        if (p.$trailingActionCount === 3) return "0 7rem 0 1rem";
        if (p.$trailingActionCount === 2) return "0 5rem 0 1rem";
        if (p.$trailingActionCount === 1 || p.$hasPickerIcon) return "0 2.9rem 0 1rem";
        return "0 1rem";
    }};
    border-radius: var(--calendar-radius-input);
    border: 1px solid ${p => p.themeMode === "dark" ? "#46516b" : "#95a6c8"} !important;
    margin: 0;
    font-size: 13px;
    font-weight: 500;
    box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.12);

    ::placeholder {
        color: ${p => p.themeMode === "dark" ? "#8f9ab1" : "#7f8da6"};
        -webkit-text-fill-color: ${p => p.themeMode === "dark" ? "#8f9ab1" : "#7f8da6"};
        opacity: 1;
        text-align: left;
    }
    }

    &&:focus,
    &&:focus-visible {
        outline: none;
        border: 1px solid ${p => p.themeMode === "dark" ? "#46516b" : "#95a6c8"} !important;
        background-color: var(--calendar-surface-tertiary);
        box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.12);
    }

    &[type="date"],
    &[type="time"] {
        appearance: none;
        -moz-appearance: textfield;
        -webkit-appearance: none;
        color: var(--calendar-text);
        -webkit-text-fill-color: var(--calendar-text);
        color-scheme: ${p => p.themeMode === "dark" ? "dark" : "light"};
        cursor: pointer;
        padding-right: 2.9rem;
    }

    &[readonly] {
        cursor: pointer;
    }

    &[type="date"]::-webkit-datetime-edit,
    &[type="date"]::-webkit-datetime-edit-fields-wrapper,
    &[type="date"]::-webkit-datetime-edit-text,
    &[type="date"]::-webkit-datetime-edit-month-field,
    &[type="date"]::-webkit-datetime-edit-day-field,
    &[type="date"]::-webkit-datetime-edit-year-field,
    &[type="time"]::-webkit-datetime-edit,
    &[type="time"]::-webkit-datetime-edit-fields-wrapper,
    &[type="time"]::-webkit-datetime-edit-text,
    &[type="time"]::-webkit-datetime-edit-hour-field,
    &[type="time"]::-webkit-datetime-edit-minute-field,
    &[type="time"]::-webkit-datetime-edit-ampm-field {
        color: var(--calendar-text);
        -webkit-text-fill-color: var(--calendar-text);
    }

    &[type="date"]::-webkit-calendar-picker-indicator,
    &[type="time"]::-webkit-calendar-picker-indicator {
        opacity: 0;
        background: transparent;
        cursor: pointer;
        width: 42px;
        height: 42px;
        margin-right: -0.9rem;
    }
`;

export const TaskFieldSelect = styled.select<ThemeModeProps>`
    width: 100%;
    height: 46px;
    border-radius: var(--calendar-radius-input);
    border: 1px solid ${p => p.themeMode === "dark" ? "#46516b" : "#95a6c8"} !important;
    background: var(--calendar-surface-tertiary);
    color: var(--calendar-text);
    padding: 0 2.9rem 0 0.9rem;
    box-sizing: border-box;
    font-size: 13px;
    font-weight: 500;
    box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.12);
    outline: none;
    appearance: none;

    &&:focus,
    &&:focus-visible {
        outline: none;
        border: 1px solid ${p => p.themeMode === "dark" ? "#46516b" : "#95a6c8"} !important;
        background-color: var(--calendar-surface-tertiary);
        box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.12);
    }
`;

export const TaskFieldArea = styled(PlaylistModalTextArea)<ThemeModeProps>`
    && {
    color: var(--calendar-text);
    background: var(--calendar-surface-tertiary);
    width: 100%;
    min-height: 120px;
    max-height: 200px;
    border-radius: var(--calendar-radius-input);
    border: 1px solid ${p => p.themeMode === "dark" ? "#46516b" : "#95a6c8"} !important;
    padding: 0.9rem 1rem;
    box-sizing: border-box;
    font-size: 13px;
    font-weight: 500;
    resize: vertical;
    box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.12);

    ::placeholder {
        color: var(--calendar-text-soft);
        text-align: left;
    }
    }

    &&:focus,
    &&:focus-visible {
        outline: none;
        border: 1px solid ${p => p.themeMode === "dark" ? "#46516b" : "#95a6c8"} !important;
        background: var(--calendar-surface-tertiary);
        box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.12);
    }
`;

export const ErrorText = styled.div`
    color: #ff8ba7;
    font-size: 11px;
    margin-top: 0.35rem;
`;
